import { Add, NotificationsOffOutlined } from "@mui/icons-material";
import { Autocomplete, Badge, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, TextField, Typography, useMediaQuery } from "@mui/material";
import { colors } from "../../../../../../theme/muiColors";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useModals } from "../../../../../../context/ModalContext";
import { setScheduleLater } from "../../../../../../redux/features/jobCreateSlice";
import { ArrivalDateTimeComponent, EArrivalTimeType } from "../../../../../common/ArrivalDateTime/Container";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { useCurrentCompanyTime } from "../../../../../common/CompanyTime";
import { getTimelineClickedTime } from "../../../../../jobs2/config/timeline.constants";
import OrganizationReminders from "../../../../../organization-reminder/components/principals/OrganizationReminders";
import { useRemindersQuery } from "../../../../../organization-reminder/hooks/useRemindersQuery";
import { useCompanyInfo } from "../../../../hooks/useCompany";
import { AnimatedBell } from "../components/reminders/AnimatedBell";
import ManageReminders from "../components/reminders/ManageReminders";
import { AddOptionIconButton, NotificationSwitch } from "../components/reminders/NotificationSwitch";
import { RepetitiveExecution } from "../components/repetitive-execution/RepetitiveExecution";
import { SchedulerModeSelector } from "../components/schedule-mode-selector/ScheduleModeSelector";
import { ScheduleSelector } from "../components/schedule-selector/ScheduleSelector";
import { CompanyTimezoneModalContainer } from "../components/timezone/TimeZoneConfiguration";
import { EFrequencyType, EMode, ESchedulerMode } from "../enums/event.enums";
import { buildEventPayload } from "../helpers/eventPayloadBuilder";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  enabledModes?: EMode[];
  canSkip?: boolean;
  existingPayload?: any;
  disabledForms?: Array<
    | "hourly"
    | "eachNMinutes"
    | "eachNHours"
    | "eachNDays"
    | EFrequencyType.DAILY
    | EFrequencyType.WEEKLY
    | EFrequencyType.MONTHLY
    | EFrequencyType.YEARLY
  >;
  onPayloadChange?: (payload: any) => void;
  disableJsonPayload?: boolean;
  multipleDates?: boolean;
  /** When true, skips the first onPayloadChange (avoids overwriting quote service eventPreferences before user edits). */
  suppressInitialPayloadDispatch?: boolean;
}

const TIMEZONE_MODAL_KEY = "Organization-TimeZone-Business";
const ARRIVAL_TIME_STEP_MINUTES = 15;
const MAX_DURATION_MINUTES = 8 * 60;

const DURATION_OPTIONS = Array.from(
  { length: MAX_DURATION_MINUTES / ARRIVAL_TIME_STEP_MINUTES },
  (_, index) => (index + 1) * ARRIVAL_TIME_STEP_MINUTES,
);

const normalizeTimeZone = (timeZone?: string | null) => {
  if (timeZone === null || timeZone === undefined) return null;

  const normalized = String(timeZone).trim();
  return normalized.length > 0 ? normalized : null;
};

const roundUpToMinuteStep = (date: DateTime, minuteStep: number) => {
  const nextMinute = Math.ceil(date.minute / minuteStep) * minuteStep;
  const rounded = nextMinute >= 60 ? date.plus({ hours: 1 }).set({ minute: 0 }) : date.set({ minute: nextMinute });

  return rounded.set({ second: 0, millisecond: 0 });
};

const normalizeDurationToStep = (value: number, minuteStep = ARRIVAL_TIME_STEP_MINUTES) => {
  if (!Number.isFinite(value) || value < minuteStep) return minuteStep;
  return Math.round(value / minuteStep) * minuteStep;
};

const formatDurationLabel = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}:${String(remainder).padStart(2, "0")}`;
};

const DURATION_OPTION_LABELS = DURATION_OPTIONS.map(formatDurationLabel);

const parseDurationInput = (raw: string) => {
  const value = raw
    .trim()
    .toLowerCase()
    .replace(/\s*(minutes|minute|mins|min|hours|hour|hrs|hr)\s*$/g, "");
  if (!value) return null;

  const timeMatch = value.match(/^(\d{1,2})\s*[:.]\s*(\d{1,2})$/);
  if (timeMatch) return Number(timeMatch[1]) * 60 + Number(timeMatch[2]);

  if (/^\d+$/.test(value)) return Number(value);

  return NaN;
};

const getDurationError = (minutes: number | null) => {
  if (minutes == null || Number.isNaN(minutes)) return "Use minutes or hours:minutes. Examples: 45 min, 1:15, 1.30.";
  if (minutes < ARRIVAL_TIME_STEP_MINUTES || minutes > MAX_DURATION_MINUTES) return "Duration must be between 15 minutes and 8 hours.";
  if (minutes % ARRIVAL_TIME_STEP_MINUTES !== 0) return "Duration must be in 15-minute blocks.";
  return "";
};

export const EventSchedulerView = ({
  enabledModes,
  existingPayload,
  disabledForms = [],
  onPayloadChange,
  disableJsonPayload = true,
  multipleDates = true,
  canSkip = true,
  suppressInitialPayloadDispatch = false,
}: Props) => {
  const [configureLater, setConfigureLater] = useState(false);
  const [startDateRepetitive, setStartDateRepetitive] = useState<string | null>(existingPayload?.startDate ?? null);
  const [useCompanyWallTime, setUseCompanyWallTime] = useState(Boolean(existingPayload?.frequency?.wallTime));
  const [ambiguousPolicy, setAmbiguousPolicy] = useState<"earlier" | "later">(existingPayload?.frequency?.wallTime?.ambiguous || "earlier");
  const [nonexistentPolicy, setNonexistentPolicy] = useState<"skip" | "shiftForward">(existingPayload?.frequency?.wallTime?.nonexistent || "skip");
  const [active, setActive] = useState(false);
  const allModes: EMode[] = [EMode.SINGLE_EXECUTION, EMode.REPETITIVE, EMode.CONFIGURE_LATER];
  const dispatch = useDispatch();
  const companyData = useCompanyInfo();
  const { openModal, closeByKey } = useModals();
  const isXs = useMediaQuery("(max-width:600px)");
  const currentCompanyTimeZone = useSelector((state: any) => state.userState.company?.timeZone ?? null);
  const [savedTimeZone, setSavedTimeZone] = useState<string | null>(null);
  const companyTimeZone =
    normalizeTimeZone(savedTimeZone) ?? normalizeTimeZone(companyData.data?.timeZone) ?? normalizeTimeZone(currentCompanyTimeZone);
  const currentCompanyTime = useCurrentCompanyTime();
  const selectedReminders = useSelector((state: any) => state.jobCreateStepper.selectedReminders);

  // FALTABA ESTO
  const { data: reminders } = useRemindersQuery();

  // BACKEND
  const availableReminders = (reminders || []).filter((r: any) => !r.isDeleted && r.isActive);
  const hasAvailableReminders = availableReminders.length > 0;

  // REDUX (JOB)
  const selectedArray = Object.values(selectedReminders || {}).filter(Boolean);

  const activeSelectedReminders = selectedArray.filter((r: any) => r.isActive);

  const hasActiveReminders = activeSelectedReminders.length > 0;
  const activeCount = activeSelectedReminders.length;

  const needsTimeZoneConfiguration = useMemo(() => {
    if (companyData.isLoading) return false;

    return !companyTimeZone;
  }, [companyData.isLoading, companyTimeZone]);

  const handleOpenModalAccount = useCallback(() => {
    openModal(TIMEZONE_MODAL_KEY);
  }, [openModal]);

  const handleCloseModalAccount = useCallback(() => {
    closeByKey(TIMEZONE_MODAL_KEY);
  }, [closeByKey]);

  useEffect(() => {
    if (!needsTimeZoneConfiguration) return;

    const timer = window.setTimeout(() => {
      handleOpenModalAccount();
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [needsTimeZoneConfiguration, handleOpenModalAccount]);

  const handleTimezoneModalClose = useCallback(() => {
    handleCloseModalAccount();

    if (needsTimeZoneConfiguration) {
      window.setTimeout(() => {
        handleOpenModalAccount();
      }, 600);
    }
  }, [needsTimeZoneConfiguration, handleCloseModalAccount, handleOpenModalAccount]);

  const handleTimezoneSaved = useCallback(
    (payload: { timeZone: string }) => {
      setSavedTimeZone(payload.timeZone);
      handleCloseModalAccount();
    },
    [handleCloseModalAccount],
  );

  const allowedModes = enabledModes?.length ? allModes.filter((m) => enabledModes.includes(m)) : allModes;

  const isModeDisabled = (m: EMode) => !allowedModes.includes(m);

  const [mode, setMode] = useState<EMode>(() => {
    const payloadMode = existingPayload?.mode;
    return payloadMode && Object.values(EMode).includes(payloadMode) ? payloadMode : EMode.SINGLE_EXECUTION;
  });
  const [schedulerMode, setSchedulerMode] = useState<ESchedulerMode>(ESchedulerMode.FIXED);

  const [fixedTimes, setFixedTimes] = useState<string[]>([existingPayload?.frequency?.at || "10:00"]);
  const [bufferedTimes, setBufferedTimes] = useState<Array<{ start: string; end: string }>>([{ start: "08:00", end: "10:00" }]);

  const [oneTimeFixedDates, setOneTimeFixedDates] = useState<string[]>(() => {
    const timelineDate = getTimelineClickedTime();
    if (timelineDate && !existingPayload) {
      const parsed = dayjs(timelineDate);
      if (parsed.isValid()) return [parsed.toISOString()];
    }
    return [];
  });
  const [oneTimeBufferedDates, setOneTimeBufferedDates] = useState<string[]>([]);

  const [duration, setDuration] = useState<number | null>(() => {
    const existingDuration = existingPayload?.frequency?.duration;
    if (existingDuration != null) {
      const num = Number(existingDuration);
      return isNaN(num) ? 90 : normalizeDurationToStep(num);
    }
    return 90;
  });
  const [durationDraft, setDurationDraft] = useState(() => formatDurationLabel(duration));
  const [durationError, setDurationError] = useState("");

  const handleDurationInput = useCallback((value: string, commitLabel = false) => {
    setDurationDraft(value);

    if (value === "") {
      setDuration(null);
      setDurationError("");
      return;
    }

    const minutes = parseDurationInput(value);
    const error = getDurationError(minutes);
    setDurationError(error);

    if (!error && minutes != null) {
      setDuration(minutes);
      if (commitLabel) setDurationDraft(formatDurationLabel(minutes));
    }
  }, []);

  const [singleDate, setSingleDate] = useState<string>(() => {
    const timelineDate = getTimelineClickedTime();
    if (timelineDate && !existingPayload) {
      const parsed = dayjs(timelineDate);
      if (parsed.isValid()) return parsed.toISOString();
    }
    const lsDate = localStorage.getItem("jobCreateSelectedDate");
    if (lsDate && !existingPayload) {
      const parsed = dayjs(lsDate);
      if (parsed.isValid()) {
        localStorage.removeItem("jobCreateSelectedDate");
        return parsed.toISOString();
      }
    }
    return existingPayload?.startDate || currentCompanyTime.toISO({ includeOffset: false }) || new Date().toISOString();
  });

  const canConfigureLater = !isModeDisabled(EMode.CONFIGURE_LATER);

  const [repetitiveData, setRepetitiveData] = useState(() => {
    const freq = existingPayload?.frequency;
    const intervals = freq?.interval || [];
    const initialStart = DateTime.fromISO(existingPayload?.startDate || "", { setZone: true });
    const initialEndDate =
      existingPayload?.endDate ||
      (initialStart.isValid
        ? initialStart.plus({ months: 2 }).toISO({ includeOffset: true })
        : DateTime.now().plus({ months: 2 }).setZone("UTC-6").toISO({ includeOffset: true }));

    return {
      frequencyType: freq?.type || EFrequencyType.WEEKLY,
      intervalValue: 1,
      at: freq?.at || "09:30",
      weekdays: freq?.type === "weekly" ? intervals.filter((i) => i.type === "weekday").map((i) => i.value) : [],
      monthDays: freq?.type === "monthly" ? intervals.filter((i) => i.type === "date").map((i) => i.value) : [],
      yearDates: freq?.type === "yearly" ? intervals.filter((i) => i.type === "date").map((i) => i.value) : [],
      endDate: initialEndDate,
      each: freq?.each || 1,
      exclusions: freq?.exclusionRules || [],
    };
  });

  const handleRepetitiveChange = (data: typeof repetitiveData) => {
    setRepetitiveData(data);
  };

  const handleModeChange = (newMode: EMode) => {
    setMode(newMode);
    if (newMode === EMode.SINGLE_EXECUTION) {
      setSchedulerMode(ESchedulerMode.FIXED);
      setOneTimeFixedDates([]);
      setOneTimeBufferedDates([]);
    } else {
      setSchedulerMode(ESchedulerMode.FIXED);
      setFixedTimes(["10:00"]);
      setBufferedTimes([{ start: "08:00", end: "10:00" }]);
    }
  };

  const handleSchedulerModeChange = (newSchedulerMode: ESchedulerMode) => {
    setSchedulerMode(newSchedulerMode);
    if (mode === EMode.SINGLE_EXECUTION) {
      if (newSchedulerMode === ESchedulerMode.FIXED) {
        setOneTimeBufferedDates([]);
      } else {
        setOneTimeFixedDates([]);
      }
    } else {
      if (newSchedulerMode === ESchedulerMode.FIXED) {
        setBufferedTimes([]);
      } else {
        setFixedTimes([]);
      }
    }
  };

  const handleAddOneTimeExactOption = () => {
    const base =
      oneTimeFixedDates.length > 0 ? DateTime.fromISO(oneTimeFixedDates[oneTimeFixedDates.length - 1]).plus({ hours: 1 }) : currentCompanyTime;

    const rounded = roundUpToMinuteStep(base, ARRIVAL_TIME_STEP_MINUTES);

    setOneTimeFixedDates([...oneTimeFixedDates, rounded.toISO({ includeOffset: false })!]);
  };

  const handleAddOneTimeArrivalWindowOption = () => {
    if (oneTimeBufferedDates.length > 0) {
      const lastStart = DateTime.fromISO(oneTimeBufferedDates[oneTimeBufferedDates.length - 2]);
      const nextDate = lastStart.plus({ days: 1 });

      setOneTimeBufferedDates([
        ...oneTimeBufferedDates,
        nextDate.toISO({ includeOffset: false })!,
        nextDate.plus({ hours: 1 }).toISO({ includeOffset: false })!,
      ]);
      return;
    }

    const now = currentCompanyTime;
    const rounded = roundUpToMinuteStep(now, ARRIVAL_TIME_STEP_MINUTES);

    setOneTimeBufferedDates([rounded.toISO({ includeOffset: false })!, rounded.plus({ hours: 1 }).toISO({ includeOffset: false })!]);
  };

  const getRepetitiveDisplayData = useMemo(() => {
    const baseDate = startDateRepetitive?.slice(0, 10) || currentCompanyTime.toISODate();
    if (schedulerMode === ESchedulerMode.FIXED) {
      return fixedTimes.map((time) => {
        const dt = DateTime.fromISO(`${baseDate}T${time}:00`);
        return dt.toISO() || `${baseDate}T${time}:00`;
      });
    } else {
      return bufferedTimes.flatMap((bt) => [
        DateTime.fromISO(`${baseDate}T${bt.start}:00`).toISO() || `${baseDate}T${bt.start}:00`,
        DateTime.fromISO(`${baseDate}T${bt.end}:00`).toISO() || `${baseDate}T${bt.end}:00`,
      ]);
    }
  }, [schedulerMode, fixedTimes, bufferedTimes, currentCompanyTime, startDateRepetitive]);

  const handleRepetitiveStartDateChange = (value: Dayjs | Date | string | null) => {
    if (!value) {
      setStartDateRepetitive(null);
      return;
    }

    const d = dayjs.isDayjs(value) ? value : dayjs(value);

    const formatted = d.tz(dayjs.tz.guess()).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ");

    setStartDateRepetitive(formatted);
    setRepetitiveData((previous) => {
      const previousEnd = DateTime.fromISO(previous.endDate || "");
      if (previousEnd.isValid && previousEnd > DateTime.fromISO(formatted)) return previous;
      return { ...previous, endDate: DateTime.fromISO(formatted).plus({ months: 2 }).toISO({ includeOffset: true }) || previous.endDate };
    });
  };

  // Convertir datos de display (ISO) de vuelta a HH:mm
  const handleRepetitiveTimesChange = (newData: string[]) => {
    if (schedulerMode === ESchedulerMode.FIXED) {
      const times = newData.map((iso) => DateTime.fromISO(iso).toFormat("HH:mm"));
      setFixedTimes(times);
    } else {
      // Para buffered, procesar pares
      const buffered: Array<{ start: string; end: string }> = [];
      for (let i = 0; i < newData.length; i += 2) {
        const start = DateTime.fromISO(newData[i]).toFormat("HH:mm");
        const end = DateTime.fromISO(newData[i + 1]).toFormat("HH:mm");
        buffered.push({ start, end });
      }
      setBufferedTimes(buffered);
    }
  };

  const payload = useMemo(() => {
    if (configureLater) {
      return {
        eventPreferences: {
          eventConfig: {
            mode: EMode.CONFIGURE_LATER,
          },
        },
      };
    }

    if (mode === EMode.SINGLE_EXECUTION) {
      const data = schedulerMode === ESchedulerMode.FIXED ? oneTimeFixedDates : oneTimeBufferedDates;

      return buildEventPayload({
        mode,
        startDate: singleDate,
        schedulerMode,
        fixedTimes: data.length > 0 ? data : undefined,
        duration: duration ?? undefined,
      });
    }

    return buildEventPayload({
      mode,
      startDate: startDateRepetitive ? startDateRepetitive : undefined,
      ...repetitiveData,
      wallTime:
        useCompanyWallTime &&
        companyTimeZone &&
        [EFrequencyType.WEEKLY, EFrequencyType.MONTHLY, EFrequencyType.YEARLY].includes(repetitiveData.frequencyType as EFrequencyType)
          ? { timeZone: companyTimeZone, ambiguous: ambiguousPolicy, nonexistent: nonexistentPolicy }
          : undefined,
      schedulerMode,
      fixedTimes: schedulerMode === ESchedulerMode.FIXED ? fixedTimes : undefined,
      bufferedTimes: schedulerMode === ESchedulerMode.BUFFERED ? bufferedTimes : undefined,
      duration: duration ?? undefined,
    });
  }, [
    configureLater,
    mode,
    repetitiveData,
    singleDate,
    schedulerMode,
    fixedTimes,
    bufferedTimes,
    oneTimeFixedDates,
    oneTimeBufferedDates,
    duration,
    useCompanyWallTime,
    companyTimeZone,
    ambiguousPolicy,
    nonexistentPolicy,
    startDateRepetitive,
  ]);

  const initialPayloadSkippedRef = useRef(false);

  useEffect(() => {
    if (!onPayloadChange) return;
    if (suppressInitialPayloadDispatch && !initialPayloadSkippedRef.current) {
      initialPayloadSkippedRef.current = true;
      return;
    }
    onPayloadChange(payload);
  }, [payload, onPayloadChange, suppressInitialPayloadDispatch]);

  const onSendToEventScheduler = async () => {
    // await sendEventSchedulerPayload(payload);
  };

  const isWindowMode = schedulerMode === ESchedulerMode.BUFFERED;

  const displayData = useMemo(() => {
    if (mode === EMode.SINGLE_EXECUTION) {
      const data = isWindowMode ? oneTimeBufferedDates : oneTimeFixedDates;
      return data;
    } else {
      // Para REPETITIVE, usar datos convertidos
      return getRepetitiveDisplayData;
    }
  }, [mode, isWindowMode, oneTimeBufferedDates, oneTimeFixedDates, getRepetitiveDisplayData]);

  const isDisabled = configureLater;

  return (
    <Paper
      onClick={(e) => e.stopPropagation()}
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 3,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        width: "100%",
      }}
    >
      {enabledModes?.length === 1 ? (
        <></>
      ) : (
        // <ScheduleSelector isModeDisabled={isModeDisabled} isDisabled={isDisabled} value={mode} onChange={handleModeChange} />
        <ScheduleSelector isModeDisabled={isModeDisabled} isDisabled={isDisabled} value={mode} onChange={handleModeChange} />
      )}

      {canConfigureLater && (
        <Box
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            backgroundColor: configureLater ? "var(--fx-surface-sunken, #F8FAFC)" : "transparent",
            transition: "background-color 0.2s ease",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={configureLater}
                onChange={(e) => {
                  setConfigureLater(e.target.checked);
                  dispatch(setScheduleLater(e.target.checked));
                }}
                sx={{
                  "&.Mui-checked": {
                    color: colors.orange[400],
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Configure schedule later
              </Typography>
            }
          />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "var(--fx-surface-texttertiary, #94A3B8)",
              ml: 4.5,
              mt: 0.25,
            }}
          >
            Scheduling fields will be disabled. You can complete this step later.
          </Typography>
        </Box>
      )}

      <Box display={"flex"} alignItems="center" gap={1} justifyContent="space-between">
        <Box
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            transition: "all 0.2s ease",
            backgroundColor: isDisabled ? "var(--fx-surface-sunken, #F8FAFC)" : "var(--fx-surface-base, #FFFFFF)",
            opacity: isDisabled ? 0.6 : 1,
            pointerEvents: isDisabled ? "none" : "auto",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              {hasActiveReminders ? (
                <AnimatedBell active={hasActiveReminders && !isDisabled} />
              ) : (
                <NotificationsOffOutlined color="action" sx={{ fontSize: 28 }} />
              )}

              <Badge
                badgeContent={activeCount}
                invisible={activeCount === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: colors.orange[500],
                    color: "white",
                    right: -16,
                    top: 8,
                    fontWeight: 700,
                    fontSize: 10,
                    height: 18,
                    minWidth: 18,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Notifications</Typography>
              </Badge>
            </Box>

            <BaseModalComponent
              modalKey="Organization-Reminder"
              elementTrigger={
                !hasAvailableReminders ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      color: colors.orange[500],
                      borderColor: colors.orange[500],
                      "&:hover": { borderColor: colors.orange[500], bgcolor: colors.orange[50] },
                    }}
                  >
                    Configure
                  </Button>
                ) : (
                  <NotificationSwitch disabled={isDisabled} checked={hasActiveReminders} />
                )
              }
            >
              {!hasAvailableReminders ? <OrganizationReminders /> : <ManageReminders />}
            </BaseModalComponent>
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              ml: 4.5,
              mt: 0.5,
              color: hasActiveReminders ? "text.primary" : "text.secondary",
              transition: "color 0.2s ease",
            }}
          >
            {!hasAvailableReminders
              ? "No reminders configured. Click configure to add one."
              : !hasActiveReminders
                ? "You have reminders, but none active for this job."
                : "Notifications are active for this job."}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <SchedulerModeSelector isDisabled={isDisabled} value={schedulerMode} onChange={handleSchedulerModeChange} timeZone={companyTimeZone} />
        <ArrivalDateTimeComponent
          isDisabled={isDisabled}
          multiplesDateTimes={multipleDates}
          value={displayData}
          handleRepetitiveStartDateChange={handleRepetitiveStartDateChange}
          onChange={mode === EMode.REPETITIVE ? handleRepetitiveTimesChange : isWindowMode ? setOneTimeBufferedDates : setOneTimeFixedDates}
          dateEnabled={mode === EMode.REPETITIVE ? false : true}
          type={isWindowMode ? EArrivalTimeType.WINDOW : EArrivalTimeType.EXACT}
          mode={mode}
          minuteStep={ARRIVAL_TIME_STEP_MINUTES}
          headerAction={
            <AddOptionIconButton
              disabled={isDisabled}
              onClick={
                mode === EMode.REPETITIVE
                  ? () => {
                      if (schedulerMode === ESchedulerMode.FIXED) {
                        setFixedTimes([...fixedTimes, "10:00"]);
                      } else {
                        setBufferedTimes([...bufferedTimes, { start: "08:00", end: "10:00" }]);
                      }
                    }
                  : isWindowMode
                    ? handleAddOneTimeArrivalWindowOption
                    : handleAddOneTimeExactOption
              }
              ariaLabel="Add arrival option"
            />
          }
        />
      </Box>

      {mode === EMode.REPETITIVE && (
        <>
          <RepetitiveExecution disabled={isDisabled} initialData={repetitiveData} onChange={handleRepetitiveChange} disabledForms={disabledForms} />
          {[EFrequencyType.WEEKLY, EFrequencyType.MONTHLY, EFrequencyType.YEARLY].includes(repetitiveData.frequencyType as EFrequencyType) && (
            <Box sx={{ mt: 2, p: 2, border: "1px solid var(--fx-surface-border, #E2E8F0)", borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isDisabled || !companyTimeZone}
                    checked={useCompanyWallTime}
                    onChange={(event) => setUseCompanyWallTime(event.target.checked)}
                  />
                }
                label={`Keep each visit at the company's local time${companyTimeZone ? ` (${companyTimeZone})` : ""}`}
              />
              {useCompanyWallTime && (
                <>
                  <Typography variant="caption" display="block">
                    Select a start date above. Choose how to handle the two seasonal clock changes before saving this series.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
                    <TextField
                      select
                      size="small"
                      label="If an hour repeats"
                      value={ambiguousPolicy}
                      onChange={(event) => setAmbiguousPolicy(event.target.value as "earlier" | "later")}
                      disabled={isDisabled}
                    >
                      <MenuItem value="earlier">Use the first occurrence</MenuItem>
                      <MenuItem value="later">Use the second occurrence</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="If an hour does not exist"
                      value={nonexistentPolicy}
                      onChange={(event) => setNonexistentPolicy(event.target.value as "skip" | "shiftForward")}
                      disabled={isDisabled}
                    >
                      <MenuItem value="skip">Skip that occurrence</MenuItem>
                      <MenuItem value="shiftForward">Move forward by the clock change</MenuItem>
                    </TextField>
                  </Box>
                </>
              )}
            </Box>
          )}
        </>
      )}

      <Autocomplete
        freeSolo
        disabled={isDisabled}
        size="small"
        options={DURATION_OPTION_LABELS}
        value={durationDraft}
        inputValue={durationDraft}
        onChange={(_, value) => handleDurationInput(value ?? "", true)}
        onInputChange={(_, value, reason) => {
          if (reason === "reset") return;
          handleDurationInput(value);
        }}
        forcePopupIcon
        ListboxProps={{
          sx: {
            maxHeight: 240,
            py: 0,
            "& .MuiAutocomplete-option": {
              minHeight: 32,
              py: 0.5,
              px: 1.5,
              fontSize: 14,
            },
          },
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              boxShadow: "0 2px 8px rgba(0,0,0,0.22)",
            },
          },
        }}
        sx={{
          mt: 3,
          width: "100%",
          "& .MuiOutlinedInput-root": {
            minHeight: 36,
            py: 0,
            fontSize: 14,
          },
          "& .MuiAutocomplete-input": {
            py: "6px !important",
          },
          "& .MuiInputLabel-root": {
            fontSize: 12,
          },
          "& .MuiFormHelperText-root": {
            ml: 1.5,
            fontSize: 11,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Duration (hours : minutes)"
            inputProps={{
              ...params.inputProps,
              inputMode: "numeric",
            }}
            error={Boolean(durationError)}
            helperText={durationError || "Estimated service duration (this is not the arrival window)."}
            onBlur={() => {
              if (durationDraft === "") {
                setDuration(90);
                setDurationDraft(formatDurationLabel(90));
                setDurationError("");
                return;
              }

              handleDurationInput(durationDraft, true);
            }}
          />
        )}
      />
      {/* Preview del payload JSON */}
      {!disableJsonPayload && (
        <>
          <Box
            sx={{
              mt: 4,
              background: "#1d1d1f",
              color: "white",
              borderRadius: 2,
              p: 2,
              fontFamily: "monospace",
              fontSize: 13,
              whiteSpace: "pre-wrap",
              overflow: "auto",
              maxHeight: 400,
            }}
          >
            {JSON.stringify(payload, null, 2)}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              onClick={onSendToEventScheduler}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 15,
                backgroundColor: "#0071e3",
                color: "white",
                boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#0077ed",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
            >
              Send Payload to Event Scheduler
            </Button>
          </Box>
        </>
      )}
      <BaseModalComponent defaultSizes={{ width: 600, height: 550 }} modalKey={TIMEZONE_MODAL_KEY} modalLabel="Organization Time Zone">
        <CompanyTimezoneModalContainer initialTimeZone={companyTimeZone} onSaved={handleTimezoneSaved} onClose={handleTimezoneModalClose} />
      </BaseModalComponent>
    </Paper>
  );
};
